/**
 * RadialProgress
 * Animated SVG circular progress ring.
 * Used on Childprogresspage and ChurchMemberPage to show overall mastery.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface RadialProgressProps {
    /** 0–100 */
    score: number
    size?: number
    strokeWidth?: number
    accentColor?: string
    trackColor?: string
    label?: string
    sublabel?: string
    /** show animated count-up number */
    showNumber?: boolean
}

const STANDING: { min: number; label: string; color: string }[] = [
    { min: 90, label: 'Mastery', color: '#4ade80' },
    { min: 75, label: 'Proficient', color: '#60a5fa' },
    { min: 55, label: 'Developing', color: '#fbbf24' },
    { min: 0, label: 'Beginner', color: '#f87171' },
]

export function RadialProgress({
    score, size = 140, strokeWidth = 11,
    accentColor, trackColor = 'rgba(255,255,255,0.07)',
    label, sublabel, showNumber = true,
}: RadialProgressProps) {
    const { t } = useTranslation()
    const [displayScore, setDisplayScore] = useState(0)
    const animRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Derive standing
    const standing = STANDING.find(s => score >= s.min) ?? STANDING[STANDING.length - 1]
    const color = accentColor ?? standing.color

    // Count-up animation
    useEffect(() => {
        let current = 0
        const step = Math.ceil(score / 40)
        const tick = () => {
            current = Math.min(current + step, score)
            setDisplayScore(current)
            if (current < score) animRef.current = setTimeout(tick, 18)
        }
        animRef.current = setTimeout(tick, 80)
        return () => { if (animRef.current) clearTimeout(animRef.current) }
    }, [score])

    const r = (size - strokeWidth) / 2
    const cx = size / 2
    const cy = size / 2
    const circumference = 2 * Math.PI * r
    const offset = circumference - (displayScore / 100) * circumference

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    {/* Track */}
                    <circle
                        cx={cx} cy={cy} r={r}
                        fill="none" stroke={trackColor} strokeWidth={strokeWidth}
                    />
                    {/* Progress arc */}
                    <circle
                        cx={cx} cy={cy} r={r}
                        fill="none"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        style={{ transition: 'stroke-dashoffset 0.05s linear, stroke 0.4s' }}
                    />
                </svg>

                {/* Centre content */}
                {showNumber && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <span style={{ fontSize: size * 0.22, fontWeight: 900, color, lineHeight: 1, letterSpacing: '-1px' }}>
                            {displayScore}
                        </span>
                        <span style={{ fontSize: size * 0.09, color: 'rgba(255,255,255,0.35)', fontWeight: 600, letterSpacing: 0.5 }}>
                            %
                        </span>
                    </div>
                )}
            </div>

            {(label || sublabel) && (
                <div style={{ textAlign: 'center' }}>
                    {label && (
                        <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.82)', margin: 0 }}>
                            {label}
                        </p>
                    )}
                    {sublabel && (
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: '3px 0 0', fontWeight: 600 }}>
                            {sublabel}
                        </p>
                    )}
                    {/* Standing chip */}
                    <span style={{
                        display: 'inline-block', marginTop: 6,
                        fontSize: 10, fontWeight: 800, padding: '3px 10px',
                        borderRadius: 100, letterSpacing: '0.8px',
                        background: `${color}18`, color,
                        border: `1px solid ${color}40`,
                    }}>
                        {t(`progress.standing.${standing.label.toLowerCase()}`, standing.label).toUpperCase()}
                    </span>
                </div>
            )}
        </div>
    )
}
